import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import AdminLayout from '@/components/AdminLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Calendar,
  User,
  CheckCircle,
  XCircle,
  Clock,
  KeyRound,
  RefreshCw,
  MoreVertical,
  PauseCircle,
  PauseCircleIcon,
} from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useToast } from '@/hooks/use-toast'
import { API_BASE_URL, authedFetch } from '@/utils/apiConfig'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Label } from '@radix-ui/react-label'
import { getStatusIcon, getStatusText } from '@/utils/status'
import { formatLocalDate } from '@/utils/dateUtils'

interface Employee {
  id: string
  name: string
  email: string
  department: string
  role: string
  createdAt: string
  sicil?: string
  kod?: string
  magaza?: string
}

interface TestAssignment {
  id: string
  testName: string
  assignedAt: string
  dueDate: string
  accessCode?: string
  attempt_count: number
  status:
    | 'completed'
    | 'pending'
    | 'abandoned'
    | 'expired'
    | 'timeout'
    | 'camera_access_error'
    | 'in_progress'
    | 'chunk_upload_error'
    | 'renewed'
    | 'speed_test_fail'
  resultId?: string
  testId: string
}

const AdminEmployeeDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [employee, setEmployee] = useState<Employee | null>(null)
  const [testAssignments, setTestAssignments] = useState<TestAssignment[]>([])
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [resettingCode, setResettingCode] = useState<string | null>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showResetDialog, setShowResetDialog] = useState<{
    code: string | null
    completed: boolean
  }>({ code: null, completed: false })

  const [editName, setEditName] = useState('')
  const [editEmail, setEditEmail] = useState('')
  const [editDepartment, setEditDepartment] = useState('')
  const [editRole, setEditRole] = useState('')
  const [editKod, setEditKod] = useState('')

  // Fetch employee from Supabase
  const fetchEmployee = async (employeeId: string) => {
    const { data, error } = await supabase
      .from('employee')
      .select('*')
      .eq('id', employeeId)
      .single()

    if (error) {
      console.error('Error fetching employee:', error)
      return null
    }

    return data
  }

  const fetchTestAssignments = async (employeeId: string) => {
    const { data, error } = await supabase
      .from('employee_tests')
      .select(
        `
        *,
        tests (
          name
        )
      `
      )
      .eq('employee_id', employeeId)
      .neq('status', 'renewed')

    if (error) {
      console.error('Error fetching test assignments:', error)
      return []
    }

    return data || []
  }

  // Transform employee data
  const transformEmployeeData = (supabaseData: any): Employee => {
    return {
      id: supabaseData.id,
      name: supabaseData.personel || 'N/A',
      email: supabaseData.mail_adresi || 'N/A',
      department: supabaseData.magaza || 'N/A',
      role: supabaseData.pozisyon || 'N/A',
      createdAt: supabaseData.created_at || new Date().toISOString(),
      sicil: supabaseData.sicil,
      kod: supabaseData.kod,
      magaza: supabaseData.magaza,
    }
  }

  // Transform test assignment data
  const transformTestAssignments = (supabaseData: any[]): TestAssignment[] => {
    return supabaseData.map((item) => ({
      id: item.id,
      testName: item.tests.name,
      assignedAt: item.created_at || new Date().toISOString(),
      dueDate:
        item.assignment_due_date || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // Default 7 days if no completion date
      accessCode: item.pairing_code,
      status:
        item.status === 'completed'
          ? 'completed'
          : item.status === 'expired'
            ? 'expired'
            : item.status === 'abandoned'
              ? 'abandoned'
              : item.status === 'timeout'
                ? 'timeout'
                : item.status === 'camera_access_error'
                  ? 'camera_access_error'
                  : item.status === 'in_progress'
                    ? 'in_progress'
                    : item.status === 'chunk_upload_error'
                      ? 'chunk_upload_error'
                      : item.status === 'renewed'
                        ? 'renewed'
                        : item.status === 'speed_test_fail'
                          ? 'speed_test_fail'
                          : 'pending',
      resultId: item.completed_at ? item.id : undefined,
      testId: item.test_id,
      attempt_count: item.attempt_count || 1,
    }))
  }

  // Load employee and test data
  useEffect(() => {
    const loadEmployeeData = async () => {
      if (!id) return

      setLoading(true)
      try {
        const [employeeData, assignmentsData] = await Promise.all([
          fetchEmployee(id),
          fetchTestAssignments(id),
        ])

        if (employeeData) {
          const transformed = transformEmployeeData(employeeData)
          setEmployee(transformed)
          // Populate form
          setEditName(transformed.name)
          setEditEmail(transformed.email)
          setEditDepartment(transformed.department)
          setEditRole(transformed.role)
          setEditKod(transformed.kod || '')
        }
        setTestAssignments(transformTestAssignments(assignmentsData))
      } catch (error) {
        console.error('Error loading employee data:', error)
      } finally {
        setLoading(false)
      }
    }
    loadEmployeeData()
  }, [id])

  const handleSave = async () => {
    if (!employee || !id) return
    // Validation
    if (!editName.trim() || !editEmail.trim() || !editDepartment.trim() || !editRole.trim()) {
      toast({
        title: 'Eksik bilgi',
        description: 'Lütfen tüm alanları doldurun.',
        variant: 'destructive',
      })
      return
    }
    setSaving(true)
    try {
      const { error } = await supabase
        .from('employee')
        .update({
          personel: editName,
          mail_adresi: editEmail,
          magaza: editDepartment,
          pozisyon: editRole,
          kod: editKod,
        })
        .eq('id', id)
      if (error) throw error
      toast({
        title: 'Başarılı',
        description: 'Çalışan bilgileri başarıyla güncellendi.',
      })
      // Reload
      const employeeData = await fetchEmployee(id)
      if (employeeData) setEmployee(transformEmployeeData(employeeData))
      setIsEditing(false)
    } catch (error) {
      toast({
        title: 'Hata',
        description: 'Çalışan bilgileri güncellenirken hata.',
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    if (employee) {
      setEditName(employee.name)
      setEditEmail(employee.email)
      setEditDepartment(employee.department)
      setEditRole(employee.role)
      setEditKod(employee.kod || '')
    }
    setIsEditing(false)
  }

  const handleDelete = async () => {
    if (!employee) return
    const { error } = await supabase.from('employee').delete().eq('id', employee.id)
    if (error) {
      toast({
        title: 'Hata',
        description: 'Silme işlemi başarısız!',
        variant: 'destructive',
      })
      return
    }
    toast({ title: 'Başarılı', description: 'Çalışan silindi!' })
    navigate('/admin/employees')
  }

  const handleResetAccessCode = async (accessCode: string) => {
    if (!accessCode || !id) return

    const currentAssignment = testAssignments.find((a) => a.accessCode === accessCode)
    if (!currentAssignment) {
      toast({
        title: 'Hata',
        description: 'Assignment bulunamadı.',
        variant: 'destructive',
      })
      return
    }

    // If status is completed, ask for confirmation
    if (currentAssignment.status === 'completed') {
      const confirmed = window.confirm(
        'Bu sınav zaten tamamlanmış. Yenilemek istediğinize emin misiniz?'
      )
      if (!confirmed) return
    }

    setResettingCode(accessCode)
    try {
      const currentAssignment = testAssignments.find((a) => a.accessCode === accessCode)
      if (!currentAssignment) {
        throw new Error('Assignment not found')
      }

      const MAX_ATTEMPTS = 4
      if (currentAssignment.attempt_count >= MAX_ATTEMPTS) {
        toast({
          title: 'Limit Aşıldı',
          description: `Maksimum deneme sayısına (${MAX_ATTEMPTS}) ulaşıldı.`,
          variant: 'destructive',
        })
        return
      }

      // Reset same record in-place (preserves pairing_code, started_at, time_spent_seconds)
      const { error: updateError } = await supabase
        .from('employee_tests')
        .update({
          status: 'pending',
          attempt_count: currentAssignment.attempt_count + 1,
          completed_at: null,
          camera_check: false,
          updated_at: new Date().toISOString(),
        })
        .eq('id', currentAssignment.id)

      if (updateError) throw updateError

      toast({
        title: 'Başarılı',
        description: 'Erişim kodu başarıyla yenilendi.',
      })

      // Refresh test assignments data
      const assignmentsData = await fetchTestAssignments(id)
      setTestAssignments(transformTestAssignments(assignmentsData))
    } catch (error) {
      console.error('Error resetting access code:', error)
      toast({
        title: 'Hata',
        description: 'Erişim kodu yenilenirken bir hata oluştu.',
        variant: 'destructive',
      })
    } finally {
      setResettingCode(null)
    }
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </AdminLayout>
    )
  }

  if (!employee) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <h2 className="text-xl font-bold mb-4">Çalışan Bulunamadı</h2>
          <p className="text-gray-500 mb-6">
            Aradığınız çalışan bulunamadı veya silinmiş olabilir.
          </p>
          <Button asChild>
            <Link to="/admin/employees">Çalışanlara Dön</Link>
          </Button>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-gray-200 w-12 h-12 rounded-full flex items-center justify-center">
              <User className="h-6 w-6 text-gray-700" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">
                {isEditing ? 'Çalışan Düzenle' : employee?.name}
              </h1>
              <p className="text-gray-500">
                {isEditing ? 'Bilgileri güncelleyin' : employee?.email}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button variant="outline" onClick={handleCancel} disabled={saving}>
                  İptal
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
              </>
            ) : (
              <>
                <Button onClick={() => setIsEditing(true)}>Düzenle</Button>
                <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive">Sil</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Çalışanı silmek istiyor musun?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Bu işlem geri alınamaz. Çalışan kaydı kalıcı olarak silinecek.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Vazgeç</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDelete}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Sil
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                <Button variant="outline" onClick={() => navigate('/admin/employees')}>
                  Çalışan Listesine Dön
                </Button>
              </>
            )}
          </div>
        </div>

        <Tabs defaultValue="profile">
          <TabsList className="grid w-full md:w-auto grid-cols-2 md:inline-flex">
            <TabsTrigger value="profile">Profil</TabsTrigger>
            <TabsTrigger value="testhistory">Sınav Geçmişi</TabsTrigger>
          </TabsList>

          {/* Profil Tab */}
          <TabsContent value="profile" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Çalışan Bilgileri</CardTitle>
              </CardHeader>
              <CardContent>
                {isEditing ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="editName">Ad Soyad</Label>
                      <Input
                        id="editName"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="Ad ve soyad"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="editEmail">E-posta</Label>
                      <Input
                        id="editEmail"
                        type="email"
                        value={editEmail}
                        onChange={(e) => setEditEmail(e.target.value)}
                        placeholder="E-posta adresi"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="editDepartment">Departman</Label>
                      <Input
                        id="editDepartment"
                        value={editDepartment}
                        onChange={(e) => setEditDepartment(e.target.value)}
                        placeholder="Departman"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="editRole">Pozisyon</Label>
                      <Input
                        id="editRole"
                        value={editRole}
                        onChange={(e) => setEditRole(e.target.value)}
                        placeholder="Pozisyon"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="editKod">Kod</Label>
                      <Input
                        id="editKod"
                        value={editKod}
                        onChange={(e) => setEditKod(e.target.value)}
                        placeholder="Kod"
                      />
                    </div>
                    {employee.sicil && (
                      <div>
                        <Label>Sicil No</Label>
                        <div className="text-lg text-gray-400">
                          {employee.sicil} (Değiştirilemez)
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Ad Soyad</h3>
                      <p className="text-lg">{employee.name}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">E-posta</h3>
                      <p className="text-lg">{employee.email}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Departman</h3>
                      <p className="text-lg">{employee.department}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Pozisyon</h3>
                      <p className="text-lg">{employee.role}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Kod</h3>
                      <p className="text-lg">{employee.kod || 'Belirtilmemiş'}</p>
                    </div>
                    {employee.sicil && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Sicil No</h3>
                        <p className="text-lg">{employee.sicil}</p>
                      </div>
                    )}
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Kayıt Tarihi</h3>
                      <p className="text-lg flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {formatLocalDate(employee.createdAt)}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="testhistory" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Sınav Geçmişi</CardTitle>
              </CardHeader>
              <CardContent>
                {testAssignments.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Sınav Adı</TableHead>
                        <TableHead>Atanma Tarihi</TableHead>
                        <TableHead>Son Tarih</TableHead>
                        <TableHead>Erişim Kodu</TableHead>
                        <TableHead>Durum</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {testAssignments.map((assignment) => (
                        <TableRow key={assignment.id}>
                          <TableCell className="font-medium">{assignment.testName}</TableCell>
                          <TableCell>{formatLocalDate(assignment.assignedAt)}</TableCell>
                          <TableCell>{formatLocalDate(assignment.dueDate)}</TableCell>
                          <TableCell>
                            {assignment.accessCode && (
                              <div className="flex items-center gap-1.5">
                                <KeyRound className="h-4 w-4 text-gray-500" />
                                <code className="bg-gray-100 px-2 py-0.5 rounded font-mono text-sm">
                                  {assignment.accessCode}
                                </code>
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getStatusIcon(assignment.status)}
                              <span>{getStatusText(assignment.status)}</span>
                              {assignment.attempt_count > 1 && (
                                <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                  D{assignment.attempt_count}/4
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {assignment.status === 'completed' && assignment.resultId && (
                                  <DropdownMenuItem asChild>
                                    <Link
                                      to={`/admin/results/${assignment.testId}/${assignment.resultId}`}
                                    >
                                      Sonucu Görüntüle
                                    </Link>
                                  </DropdownMenuItem>
                                )}
                                {assignment.accessCode && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      setShowResetDialog({
                                        code: assignment.accessCode!,
                                        completed: assignment.status === 'completed',
                                      })
                                    }
                                    disabled={resettingCode === assignment.accessCode || assignment.attempt_count >= 4}
                                  >
                                    {resettingCode === assignment.accessCode ? (
                                      <>
                                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                        Yenileniyor...
                                      </>
                                    ) : (
                                      <>
                                        <RefreshCw className="mr-2 h-4 w-4" />
                                        Erişimi Yenile
                                      </>
                                    )}
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">Bu çalışana henüz sınav atanmamış.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        <AlertDialog
          open={!!showResetDialog.code}
          onOpenChange={(open) => !open && setShowResetDialog({ code: null, completed: false })}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {showResetDialog.completed
                  ? 'Bu sınav zaten tamamlanmış. Yenilemek istediğinize emin misiniz?'
                  : 'Erişim kodunu yenilemek istiyor musunuz?'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {showResetDialog.completed
                  ? 'Bu işlem sınavı yeniden açar. Mevcut kayıt korunur, deneme sayısı artırılır.'
                  : 'Bu işlem sınavı yeniden açar. Mevcut kayıt korunur, deneme sayısı artırılır.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Vazgeç</AlertDialogCancel>
              <AlertDialogAction
                onClick={async () => {
                  if (showResetDialog.code) {
                    await handleResetAccessCode(showResetDialog.code)
                    setShowResetDialog({ code: null, completed: false })
                  }
                }}
              >
                Yenile
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  )
}

export default AdminEmployeeDetail
